import {
  INTRANET_IMAGE_ACCEPT,
  INTRANET_IMAGE_MAX_BYTES,
  INTRANET_VIDEO_ACCEPT,
  INTRANET_VIDEO_ALLOWED_MIME,
  intranetImageMaxMb,
  type IntranetContentDetail,
} from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API_BASE_URL, ApiRequestError } from '@/lib/api';
import { IntranetImageFrame } from '@/products/intranet/components/intranet-image-frame';
import {
  coverFailureMessage,
  coverFileIssue,
} from '@/products/intranet/features/intranet/cover-file';
import {
  useDeleteIntranetCover,
  useIntranetAppearance,
  useUploadIntranetCover,
} from '@/products/intranet/features/intranet/use-intranet';

const VIDEOS = new Set<string>(INTRANET_VIDEO_ALLOWED_MIME);

/** Chosen on a new post. The form uploads it after create, on the way to the edit page. */
export type IntranetCoverDraft = { file: File; alt: string };

/** One cover slot for a news entry: picture or video, same endpoint. */
export function IntranetCoverField({
  content,
  onDraftChange,
}: {
  content: IntranetContentDetail | null;
  onDraftChange?: (draft: IntranetCoverDraft | null) => void;
}) {
  const appearance = useIntranetAppearance();
  const imageMaxBytes = appearance.data?.imageMaxBytes ?? INTRANET_IMAGE_MAX_BYTES;
  const imageMaxMb = intranetImageMaxMb(imageMaxBytes);
  const upload = useUploadIntranetCover();
  const remove = useDeleteIntranetCover();
  const [alt, setAlt] = useState(content?.coverAlt ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const pending = upload.isPending || remove.isPending;
  const status = content?.coverScanStatus ?? null;
  const stored = content !== null && (status !== null || content.coverUrl !== null);
  const showLocal = preview !== null && status !== 'BLOCKED';
  const serverSrc = content?.coverUrl ? `${API_BASE_URL}${content.coverUrl}` : null;
  const localVideo = file !== null && VIDEOS.has(file.type);
  const serverVideo = content?.coverMedia === 'video';

  function onFile(next: File | null) {
    setPreview((previous) => {
      if (previous) {
        URL.revokeObjectURL(previous);
      }
      return next ? URL.createObjectURL(next) : null;
    });
    setFile(next);
    onDraftChange?.(next ? { file: next, alt } : null);
  }

  async function onPickFile(next: File | null) {
    const refreshed = await appearance.refetch();
    if (next) {
      const limit = refreshed.data?.imageMaxBytes ?? imageMaxBytes;
      const issue = coverFileIssue(next, limit);
      if (issue) {
        toast.error(issue);
      }
    }
    onFile(next);
  }

  function onAlt(value: string) {
    setAlt(value);
    if (file) {
      onDraftChange?.({ file, alt: value });
    }
  }

  async function saveCover() {
    if (!content || !file) {
      toast.error('Bitte eine Bilddatei wählen.');
      return;
    }
    const refreshed = await appearance.refetch();
    const limit = refreshed.data?.imageMaxBytes ?? INTRANET_IMAGE_MAX_BYTES;
    const chosen = coverChoice(file, alt, limit);
    if (!chosen) {
      return;
    }
    try {
      const saved = await upload.mutateAsync({
        slug: content.slug,
        file,
        alt: chosen.alt,
      });
      if (saved.coverScanStatus !== 'PENDING') {
        onFile(null);
      }
      toast.success(
        saved.coverScanStatus === 'PENDING'
          ? saved.coverMedia === 'video'
            ? 'Video wird geprüft.'
            : 'Bild wird geprüft.'
          : 'Titelbild gespeichert.',
      );
    } catch (error) {
      toast.error(coverFailureMessage(error, limit));
    }
  }

  async function removeCover() {
    if (!content) {
      return;
    }
    try {
      await remove.mutateAsync(content.slug);
      onFile(null);
      setAlt('');
      toast.success('Titelbild entfernt.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Entfernen fehlgeschlagen.');
    }
  }

  return (
    <fieldset className="space-y-3 rounded-xl border border-border bg-card p-4 text-card-foreground shadow-sm">
      <legend className="px-1 text-sm font-medium">Titelbild oder Video hinzufügen</legend>
      {status === 'PENDING' ? (
        <p className="text-sm text-muted-foreground">
          {serverVideo ? 'Video wird geprüft' : 'Bild wird geprüft'}
        </p>
      ) : null}
      {status === 'BLOCKED' ? (
        <p role="alert" className="text-sm text-destructive">
          Das Bild wurde abgelehnt und wird nicht angezeigt.
        </p>
      ) : null}
      {showLocal && localVideo ? (
        <video src={preview ?? ''} controls className="h-auto w-full rounded-md" />
      ) : null}
      {showLocal && !localVideo ? (
        <IntranetImageFrame src={preview ?? ''} alt={alt.trim() || 'Vorschau'} />
      ) : null}
      {!showLocal && serverSrc && serverVideo ? (
        <video src={serverSrc} controls className="h-auto w-full rounded-md" />
      ) : null}
      {!showLocal && serverSrc && !serverVideo ? (
        <IntranetImageFrame src={serverSrc} alt={content?.coverAlt ?? ''} />
      ) : null}
      <div className="space-y-2">
        <Label htmlFor="intranet-cover-alt">Alternativtext</Label>
        <Input
          id="intranet-cover-alt"
          value={alt}
          maxLength={200}
          onChange={(event) => onAlt(event.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="intranet-cover-file">Datei</Label>
        <Input
          id="intranet-cover-file"
          type="file"
          accept={`${INTRANET_IMAGE_ACCEPT},${INTRANET_VIDEO_ACCEPT}`}
          onClick={() => {
            void appearance.refetch();
          }}
          onChange={(event) => void onPickFile(event.target.files?.[0] ?? null)}
        />
        <p className="text-xs text-muted-foreground">
          JPEG, PNG oder WebP bis {imageMaxMb} MB, oder ein Video unter 12 MB.
        </p>
      </div>
      {content ? (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => void saveCover()}
          >
            Titelbild speichern
          </Button>
          {stored ? (
            <Button
              type="button"
              variant="ghost"
              disabled={pending}
              onClick={() => void removeCover()}
            >
              Titelbild entfernen
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Die Datei wird mit dem Beitrag hochgeladen.</p>
      )}
    </fieldset>
  );
}

function coverChoice(file: File, alt: string, imageMaxBytes: number): { alt: string } | null {
  const issue = coverFileIssue(file, imageMaxBytes);
  if (issue) {
    toast.error(issue);
    return null;
  }
  return { alt: alt.trim() };
}
